import { useEffect, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@arabquiz/ui'
import { ListChecks, ListOrdered, ToggleLeft } from 'lucide-react'
import { McqOptionsEditor } from '../question/McqOptionsEditor'
import { TfAnswerEditor } from '../question/TfAnswerEditor'
import { OrderItemsEditor } from '../question/OrderItemsEditor'
import {
  MAX_TIME_SECONDS,
  MIN_TIME_SECONDS,
  DEFAULT_TIME_SECONDS,
  makeMcq,
  makeOrder,
  makeTf,
  type EditorQuestion,
} from '../question/types'
import { Modal } from './Modal'
import {
  DIFFICULTY_VALUES,
  questionBankKeys,
  bankItemToEditorQuestion,
  type BankCategory,
  type BankItem,
  type Difficulty,
  type QuestionType,
} from './types'

type QuestionBankItemFormProps = {
  open: boolean
  onClose: () => void
  /** Item to edit (own private), or `null` to create a new private item. */
  item: BankItem | null
  /**
   * Admin variant: target the `/admin/question-bank/items` endpoints (which
   * create **public** content and may edit ANY item) and invalidate the admin
   * bank list + analytics on save. Defaults to the normal user endpoints.
   */
  adminMode?: boolean
}

type QuestionTypeOption = {
  value: QuestionType
  icon: typeof ListChecks
  labelKey: 'addMcqQuestion' | 'addTfQuestion' | 'addOrderQuestion'
}

const TYPE_OPTIONS: QuestionTypeOption[] = [
  { value: 'MultipleChoice', icon: ListChecks, labelKey: 'addMcqQuestion' },
  { value: 'TrueFalse', icon: ToggleLeft, labelKey: 'addTfQuestion' },
  { value: 'OrderSequence', icon: ListOrdered, labelKey: 'addOrderQuestion' },
]

/**
 * Create/edit a **private** question-bank item. Reuses the same per-type
 * answer sub-editors as the quiz editor (E1 extraction) so authoring a bank
 * item and authoring a quiz question are visually and behaviourally
 * identical. Posts `UpsertQuestionBankItemRequest` to `POST /items` (create)
 * or `PUT /items/{id}` (edit own); `difficulty` is a bank-only field.
 */
export function QuestionBankItemForm({
  open,
  onClose,
  item,
  adminMode = false,
}: QuestionBankItemFormProps) {
  const { t } = useTranslation('quiz')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const textId = useId()
  const timeId = useId()
  const categoryId = useId()
  const difficultyId = useId()
  const explanationId = useId()

  const [question, setQuestion] = useState<EditorQuestion>(makeMcq)
  const [categoryValue, setCategoryValue] = useState<string | null>(null)
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null)
  const [touched, setTouched] = useState(false)

  // Re-seed local state whenever the modal opens (or the target item
  // changes) so reopening for a different item never shows stale fields.
  useEffect(() => {
    if (!open) return
    if (item) {
      setQuestion(bankItemToEditorQuestion(item))
      setCategoryValue(item.categoryId)
      setDifficulty(item.difficulty)
    } else {
      setQuestion(makeMcq())
      setCategoryValue(null)
      setDifficulty(null)
    }
    setTouched(false)
  }, [open, item])

  const categoriesQuery = useQuery({
    queryKey: questionBankKeys.categories(),
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/question-bank/categories', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load categories: ${res.status}`)
      }
      return (await res.json()) as BankCategory[]
    },
    enabled: open,
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = toUpsertPayload(question, categoryValue, difficulty)
      const apiBase = adminMode
        ? '/api/v1/admin/question-bank/items'
        : '/api/v1/question-bank/items'
      const url = item ? `${apiBase}/${encodeURIComponent(item.id)}` : apiBase
      const res = await fetch(url, {
        method: item ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        throw new Error(`Failed to save item: ${res.status}`)
      }
    },
    onSuccess: () => {
      if (adminMode) {
        // Admin edits/creates affect the cross-org list and the bank-item
        // totals on the overview.
        void queryClient.invalidateQueries({ queryKey: ['admin', 'questionBank', 'items'] })
        void queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] })
      }
      void queryClient.invalidateQueries({ queryKey: questionBankKeys.itemsRoot })
      void queryClient.invalidateQueries({ queryKey: questionBankKeys.categories() })
      onClose()
    },
  })

  const textInvalid = touched && question.text.trim().length === 0

  function switchType(next: QuestionType) {
    if (next === question.type) return
    // Preserve the prompt + timing across a type change; reset only the
    // type-specific answer payload.
    const base = { text: question.text, timeSeconds: question.timeSeconds }
    if (next === 'MultipleChoice') setQuestion({ ...makeMcq(), ...base })
    else if (next === 'TrueFalse') setQuestion({ ...makeTf(), ...base })
    else setQuestion({ ...makeOrder(), ...base })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (question.text.trim().length === 0) {
      setTouched(true)
      return
    }
    saveMutation.mutate()
  }

  const categories = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data],
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={item ? t('bankEditItem') : t('bankCreateItem')}
      footer={
        <div className="flex items-center justify-end gap-3">
          {saveMutation.isError ? (
            <span className="me-auto text-sm text-red-600" role="alert">
              {t('bankSaveError')}
            </span>
          ) : null}
          <Button type="button" variant="ghost" onClick={onClose}>
            {tCommon('cancel')}
          </Button>
          <Button
            type="submit"
            form="bank-item-form"
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? tCommon('loading') : tCommon('save')}
          </Button>
        </div>
      }
    >
      <form id="bank-item-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        {/* Type selector */}
        <fieldset>
          <legend className="block text-sm font-semibold text-brand-900 mb-2">
            {t('question')}
          </legend>
          <div className="grid grid-cols-3 gap-2">
            {TYPE_OPTIONS.map(({ value, icon: Icon, labelKey }) => {
              const active = question.type === value
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => switchType(value)}
                  className={
                    'flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ' +
                    (active
                      ? 'border-brand-300 bg-brand-50 text-brand-800 ring-1 ring-brand-200'
                      : 'border-brand-200 bg-white text-brand-700 hover:border-brand-300 hover:bg-brand-50/40')
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  <span className="truncate">{t(labelKey)}</span>
                </button>
              )
            })}
          </div>
        </fieldset>

        {/* Prompt */}
        <div>
          <label htmlFor={textId} className="block text-sm font-semibold text-brand-900 mb-1.5">
            {t('questionText')}
          </label>
          <textarea
            id={textId}
            value={question.text}
            onChange={(e) => setQuestion({ ...question, text: e.target.value })}
            onBlur={() => setTouched(true)}
            placeholder={t('questionTextPlaceholder')}
            maxLength={500}
            rows={2}
            aria-invalid={textInvalid || undefined}
            className="block w-full rounded-md border border-brand-200 bg-white ps-3 pe-3 py-2 text-sm text-brand-900 placeholder:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 resize-y"
          />
          {textInvalid ? (
            <p className="text-xs text-red-600 mt-1.5" role="alert">
              {t('questionText')}
            </p>
          ) : null}
        </div>

        {/* Category + difficulty + time */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor={categoryId} className="block text-sm font-semibold text-brand-900 mb-1.5">
              {t('bankCategory')}
            </label>
            <select
              id={categoryId}
              value={categoryValue ?? ''}
              onChange={(e) =>
                setCategoryValue(e.target.value === '' ? null : e.target.value)
              }
              className="h-10 w-full rounded-md border border-brand-200 bg-white ps-3 pe-8 text-sm text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <option value="">{t('bankUncategorized')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={difficultyId} className="block text-sm font-semibold text-brand-900 mb-1.5">
              {t('bankDifficulty')}
            </label>
            <select
              id={difficultyId}
              value={difficulty ?? ''}
              onChange={(e) =>
                setDifficulty(
                  e.target.value === '' ? null : (e.target.value as Difficulty),
                )
              }
              className="h-10 w-full rounded-md border border-brand-200 bg-white ps-3 pe-8 text-sm text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <option value="">{t('bankUncategorized')}</option>
              {DIFFICULTY_VALUES.map((value) => (
                <option key={value} value={value}>
                  {value === 'Easy'
                    ? t('bankDifficultyEasy')
                    : value === 'Medium'
                      ? t('bankDifficultyMedium')
                      : t('bankDifficultyHard')}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={timeId} className="block text-sm font-semibold text-brand-900 mb-1.5">
              {t('timeSeconds')}
            </label>
            <Input
              id={timeId}
              type="number"
              min={MIN_TIME_SECONDS}
              max={MAX_TIME_SECONDS}
              dir="ltr"
              value={question.timeSeconds}
              onChange={(e) => {
                const parsed = Number.parseInt(e.target.value, 10)
                const clamped = Number.isFinite(parsed)
                  ? Math.max(MIN_TIME_SECONDS, Math.min(MAX_TIME_SECONDS, parsed))
                  : DEFAULT_TIME_SECONDS
                setQuestion({ ...question, timeSeconds: clamped })
              }}
              className="text-center"
            />
          </div>
        </div>

        {/* Per-type answer editor */}
        {question.type === 'MultipleChoice' ? (
          <McqOptionsEditor question={question} onChange={setQuestion} />
        ) : question.type === 'TrueFalse' ? (
          <TfAnswerEditor question={question} onChange={setQuestion} />
        ) : (
          <OrderItemsEditor question={question} onChange={setQuestion} />
        )}

        {/* Optional explanation (shown to players after the reveal). */}
        <div>
          <label htmlFor={explanationId} className="block text-sm font-semibold text-brand-900 mb-1.5">
            {t('explanation')}
          </label>
          <textarea
            id={explanationId}
            value={question.explanation ?? ''}
            onChange={(e) => setQuestion({ ...question, explanation: e.target.value })}
            placeholder={t('explanationPlaceholder')}
            maxLength={1000}
            rows={2}
            className="block w-full rounded-md border border-brand-200 bg-white ps-3 pe-3 py-2 text-sm text-brand-900 placeholder:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 resize-y"
          />
        </div>
      </form>
    </Modal>
  )
}

/**
 * Build the `UpsertQuestionBankItemRequest` body from the editor question +
 * bank-only metadata. Only the answer fields relevant to the type are sent;
 * the server ignores the rest.
 */
function toUpsertPayload(
  question: EditorQuestion,
  categoryId: string | null,
  difficulty: Difficulty | null,
) {
  const base = {
    type: question.type,
    text: question.text.trim(),
    timeSeconds: question.timeSeconds,
    difficulty,
    categoryId,
    explanation: question.explanation?.trim() ? question.explanation.trim() : undefined,
  }
  if (question.type === 'MultipleChoice') {
    return {
      ...base,
      options: question.options,
      correctOptionIndex: question.correctOptionIndex,
    }
  }
  if (question.type === 'OrderSequence') {
    return { ...base, items: question.items }
  }
  return { ...base, correctAnswer: question.correctAnswer }
}
